import { useBillingServiceStore } from '@billing/AppProvider'
import { useTranslate } from '@tolgee/react'
import { ConfirmationDialog } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { useOperationEquipmentMessageHandler } from '../hooks/useCommodityMessageHandler'

export const CommoditiesDialogContainer = observer(() => {
  const { t } = useTranslate()
  const { appStore, commodityStore, commodityDialogUIStore } = useBillingServiceStore()
  const { getExceptionMessage } = useOperationEquipmentMessageHandler()

  const handleCloseDelete = () => {
    commodityDialogUIStore.toggleDelete()
  }

  const handleDelete = async () => {
    try {
      if (commodityDialogUIStore.editingCommodity) {
        const commodityName = commodityDialogUIStore.editingCommodity.name
        await commodityStore.delete(commodityDialogUIStore.editingCommodity.id)
        handleCloseDelete()

        appStore.setShowAlert('success', `${commodityName} ${t('deleted', 'deleted')}`)
      }
    } catch (error) {
      appStore.setShowAlert('error', getExceptionMessage(error))
    }
  }

  const handleCloseDeactivate = () => {
    commodityDialogUIStore.toggleDeactivate()
  }

  const handleDeactivate = async () => {
    if (commodityDialogUIStore.editingCommodity) {
      const commodityName = commodityDialogUIStore.editingCommodity.name
      await commodityStore.updateIsActiveStatus(commodityDialogUIStore.editingCommodity, false)
      handleCloseDeactivate()

      appStore.setShowAlert('success', `${commodityName} ${t('deactivated', 'Deactivated')}`)
    }
  }

  const handleCloseActivate = () => {
    commodityDialogUIStore.toggleActivate()
  }

  const handleActivate = async () => {
    if (commodityDialogUIStore.editingCommodity) {
      const commodityName = commodityDialogUIStore.editingCommodity.name
      await commodityStore.updateIsActiveStatus(commodityDialogUIStore.editingCommodity, true)
      handleCloseActivate()

      appStore.setShowAlert('success', `${commodityName} ${t('activated', 'Activated')}`)
    }
  }

  return (
    <>
      <ConfirmationDialog
        open={commodityDialogUIStore.isDeleteOpen}
        title={t('deleteEntity', 'Delete {entity}', {
          entity: commodityDialogUIStore.editingCommodity?.name ?? t('commodity'),
        })}
        message={t(
          'deletingThisItemWillAlsoPermanentlyDeleteAllItsSubItems',
          'Deleting this item will also permanently delete all its sub-items. This action is irreversible.',
        )}
        primaryActionText={t('yesDelete', 'Yes, delete')}
        closeLabel={t('cancel', 'Cancel')}
        maxWidth='xs'
        blockClickOutside
        onConfirm={handleDelete}
        onClose={handleCloseDelete}
      />

      <ConfirmationDialog
        open={commodityDialogUIStore.isDeactivateOpen}
        title={t('deactivate{entity}', 'Deactivate {entity}', {
          entity: commodityDialogUIStore.editingCommodity?.name ?? t('commodity'),
        })}
        message={t(
          'deactivatingThisItemWillAlsoPermanentlyDeleteAllItsSubItems',
          'Deactivating this item will also deactivate all its sub-items.',
        )}
        primaryActionText={t('yesDeactive', 'Yes, deactive')}
        closeLabel={t('cancel', 'Cancel')}
        maxWidth='xs'
        blockClickOutside
        onConfirm={handleDeactivate}
        onClose={handleCloseDeactivate}
      />

      <ConfirmationDialog
        open={commodityDialogUIStore.isActivateOpen}
        title={t('activate{entity}', 'Activate {entity}', {
          entity: commodityDialogUIStore.editingCommodity?.name ?? t('commodity'),
        })}
        message={t(
          'activatingThisItemWillAlsoPermanentlyDeleteAllItsSubItems',
          'Activating this item will also activate all its sub-items.',
        )}
        primaryActionText={t('yesActivate', 'Yes, activate')}
        closeLabel={t('cancel', 'Cancel')}
        maxWidth='xs'
        blockClickOutside
        onConfirm={handleActivate}
        onClose={handleCloseActivate}
      />
    </>
  )
})
